import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';
import { Loader } from 'lucide-react';
import { ensureProfile } from '../services/profileService';
import { setAnalyticsUser, trackEventOnce } from '../utils/visitorTracking';
/**
 * OAuth Callback Handler
 * This page handles the redirect from Google OAuth
 * and processes the authentication tokens
 */
export function AuthCallbackPage() {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    handleOAuthCallback();
  }, []);
  const handleOAuthCallback = async () => {
    try {
      // Get the session from the URL hash
      const {
        data,
        error
      } = await supabase.auth.getSession();
      if (error) {
        throw error;
      }
      if (data.session) {
        console.log('✅ OAuth successful, user:', data.session.user.email);
        const user = data.session.user;
        // Ensure profile exists in the profiles table
        // This is a safety net in case the database trigger failed
        const fullName = user.user_metadata?.full_name || user.user_metadata?.fullName || '';
        const nameParts = fullName.split(' ');
        const firstName = user.user_metadata?.firstName || user.user_metadata?.first_name || nameParts[0] || '';
        const lastName = user.user_metadata?.lastName || user.user_metadata?.last_name || nameParts.slice(1).join(' ') || '';
        await ensureProfile(user.id, {
          firstName,
          lastName,
          fullName: fullName || `${firstName} ${lastName}`.trim(),
          email: user.email
        });
        setAnalyticsUser(user.id);
        // Check if user has completed onboarding
        const hasCompletedOnboarding = user.user_metadata?.completedOnboarding;
        if (!hasCompletedOnboarding) {
          // Google sign-ups only reach here once with onboarding incomplete,
          // and the event is deduped by user id on top of that.
          void trackEventOnce(`signup_completed:${user.id}`, 'signup_completed', {
            user_id: user.id,
            method: 'google'
          });
        }
        // Redirect based on onboarding status
        if (hasCompletedOnboarding) {
          navigate('/dashboard');
        } else {
          navigate('/onboarding');
        }
      } else {
        // No session found, redirect to login
        console.warn('⚠️ No session found after OAuth');
        navigate('/login');
      }
    } catch (err: any) {
      console.error('❌ OAuth callback error:', err);
      setError(err.message || 'Authentication failed');
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    }
  };
  if (error) {
    return <div className="flex flex-col items-center justify-center min-h-screen bg-[#f8f6f2] px-5" data-id="element-3485" data-mp="14borlbns21ed">
        <div className="bg-white rounded-xl shadow-sm p-8 max-w-md w-full text-center" data-id="element-3486" data-mp="u8t0fr9xl90i">
          <div className="text-red-600 text-5xl mb-4" data-id="element-3487" data-mp="z6l23d0do9hw">⚠️</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-3488" data-mp="z4rt3r2ald6q">
            Authentication Failed
          </h1>
          <p className="text-gray-600 mb-4" data-id="element-3489" data-mp="18wxkozomtsle">{error}</p>
          <p className="text-sm text-gray-500" data-id="element-3490" data-mp="18yh06o2grzm4">Redirecting to login page...</p>
        </div>
      </div>;
  }
  return <div className="flex flex-col items-center justify-center min-h-screen bg-[#f8f6f2]" data-id="element-3491" data-mp="1d0lqbctpee40">
      <div className="bg-white rounded-xl shadow-sm p-8 max-w-md w-full text-center" data-id="element-3492" data-mp="1hvre6h6o22uq">
        <Loader className="animate-spin h-12 w-12 text-purple-600 mx-auto mb-4" data-id="element-3493" data-mp="1mx89zyaamz4g" />
        <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-3494" data-mp="1ruqnm1z9a2is">
          Completing Sign In...
        </h1>
        <p className="text-gray-600" data-id="element-3495" data-mp="3opd9buu4gyp7">
          Please wait while we set up your account
        </p>
      </div>
    </div>;
}